import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { db } from "@/firebase";
import { v4 } from "uuid";
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { pick, random } from "lodash";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { RootState } from "@/store/store";
import { updateOrder } from "@/features/order/slice";
import { ArchivedOrder, CompletedOrder, Order, OrderStatus } from "@/types";
import { fetchUsers } from "@/store/usersSlice";

export const ordersApi = createApi({
  reducerPath: "orders",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Order"],
  endpoints: (builder) => ({
    fetchOrders: builder.query({
      queryFn: async (organizationId: string, { dispatch }) => {
        const TASK = "Órdenes";

        try {
          dispatch(addLoadingTask(TASK));
          const collectionRef = collection(db, `organizations/${organizationId}/orders`);

          const response = await getDocs(collectionRef).then((querySnapshot) => {
            return querySnapshot.docs.reduce((acc, doc: any) => {
              return [...acc, { ...doc.data(), id: doc.id }];
            }, [] as Order[]);
          });
          return { data: response };
        } catch (error) {
          return { error };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      providesTags: ["Order"],
    }),
    getOrderById: builder.query({
      queryFn: async ({ organizationId, orderId }: { organizationId: string; orderId: Order["id"] }, { dispatch }) => {
        const TASK = "Orden por Id";
        try {
          dispatch(addLoadingTask(TASK));
          const docRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
          const snapshot = await getDoc(docRef);

          if (snapshot.exists()) {
            const responsePayload = { ...snapshot.data(), id: snapshot.id };

            if (snapshot.data().createdBy) {
              dispatch(fetchUsers([snapshot.data().createdBy]));
            }
            dispatch(updateOrder(responsePayload));
            return { data: responsePayload };
          } else {
            return {
              error: {
                message: "Order not found",
                status: 404,
              },
            };
          }
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
    }),
    updateOrderStatus: builder.mutation({
      queryFn: async ({ organizationId, orderId, status }: { organizationId: string; orderId: string; status: string }, { getState, dispatch }) => {
        const TASK = "Actualizar Orden";

        try {
          dispatch(addLoadingTask(TASK));

          const store = getState() as RootState;

          if (!store.auth?.user?.uid || !organizationId || !orderId || !status) {
            throw new Error("Invalid Payload");
          }

          const docRef = doc(db, `organizations/${String(organizationId)}/orders/${String(orderId)}`);

          const payload = {
            status,
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          const response = await updateDoc(docRef, payload);

          return { data: response };
        } catch (error) {
          console.error(error);
          return { error };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Order"],
    }),
    updateOrder: builder.mutation({
      queryFn: async ({ order, organizationId }: { order: Partial<Order>; organizationId: string }, { getState, dispatch }) => {
        const TASK = "Actualizar Orden";

        try {
          dispatch(addLoadingTask(TASK));

          const store = getState() as RootState;

          const ls = localStorage.getItem("user");
          const lsUser = JSON.parse(ls ?? "{}");

          if (!lsUser || !organizationId || !order?.id) {
            throw new Error("Invalid Payload");
          }

          const docRef = doc(db, `organizations/${String(organizationId)}/orders/${String(order.id)}`);

          const ALLOWED_FIELDS = [
            "lastUpdatedBy",
            "lastUpdatedAt",
            "status",
            "items",
            "tableId",
            "subtotal",
            "taxes",
            "areTipsIncluded",
            "total",
            "paymentDetails",
            "deliveryLocation",
            "client",
            "tipPercentage",
            "number",
          ];

          const payload = {
            ...pick(order, ALLOWED_FIELDS),
            lastUpdatedBy: store?.auth?.user?.uid ?? lsUser,
            lastUpdatedAt: Date.now(),
          };

          const response = await updateDoc(docRef, payload);

          dispatch(updateOrder(payload));

          return { data: response };
        } catch (error) {
          console.error(error);
          return { error };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Order"],
    }),
    addOrder: builder.mutation({
      queryFn: async (organizationId: string, { getState, dispatch }) => {
        const TASK = "Agregar Orden";
        try {
          const store = getState() as RootState;
          dispatch(addLoadingTask(TASK));

          if (!store.auth?.user?.uid || !organizationId) {
            throw new Error("Invalid Payload");
          }

          const collectionRef = collection(db, `organizations/${String(organizationId)}/orders`);

          const subtotal = random(200, 1000);

          const payload = {
            number: random(200, 300),
            status: "reviewing",
            subtotal,
            taxes: subtotal * 0.15,
            areTipsIncluded: false,
            total: subtotal + subtotal * 0.15,
            createdBy: store.auth.user.uid,
            createdAt: Date.now(),
            items: [
              {
                id: v4(),
                title: "Pierna con muslo",
                price: 100,
                quantity: 1,
              },
              {
                id: v4(),
                title: "Gallopinto",
                price: 80,
                quantity: 2,
              },
            ],
            user: {
              name: "Maria",
              lastname: "Perez",
              email: "maria@gmail.com",
              phone: "50512345678",
            },
          };

          const response = await addDoc(collectionRef, payload);
          return { data: response };
        } catch (error) {
          console.error(error);
          return { error };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Order"],
    }),

    archiveOrder: builder.mutation({
      queryFn: async ({ orderId, organizationId }: { orderId: ArchivedOrder["id"]; organizationId: string }, { getState, dispatch }) => {
        const TASK = "Archivar Orden";

        try {
          dispatch(addLoadingTask(TASK));

          const store = getState() as RootState;

          const ls = localStorage.getItem("user");
          const lsUser = JSON.parse(ls ?? "{}");
          if (!lsUser || !organizationId || !orderId) {
            throw new Error("Invalid Payload");
          }
          const ALLOWED_FIELDS = [
            "archivedAt",
            "archivedBy",
            "lastUpdatedBy",
            "lastUpdatedAt",
            "status",
            "items",
            "tableId",
            "subtotal",
            "taxes",
            "areTipsIncluded",
            "total",
            "paymentDetails",
            "deliveryLocation",
            "client",
          ];

          const archivedOrdersRef = doc(db, `organizations/${String(organizationId)}/archivedOrders/${String(orderId)}`);

          const payload = {
            ...pick(store.order, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth?.user?.uid ?? lsUser,
            lastUpdatedAt: Date.now(),
            archivedBy: store.auth?.user?.uid ?? lsUser,
            archivedAt: Date.now(),
            status: OrderStatus.ARCHIVED,
          };

          const response: any = await setDoc(archivedOrdersRef, payload);
          const unarchivedOrdersRef = doc(db, `organizations/${String(organizationId)}/orders/${String(orderId)}`);
          await deleteDoc(unarchivedOrdersRef);

          return { data: response };
        } catch (error) {
          console.error(error);
          return { error };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
    }),

    completeOrder: builder.mutation({
      queryFn: async ({ order, orderId, organizationId }: { order: CompletedOrder; orderId: CompletedOrder["id"]; organizationId: string }, { getState, dispatch }) => {
        const TASK = "Orden Completada";

        try {
          dispatch(addLoadingTask(TASK));

          const store = getState() as RootState;

          if (!order || !store.auth.user || !organizationId || !orderId) {
            throw new Error("Invalid Payload");
          }
          const ALLOWED_FIELDS = [
            "completedAt",
            "completedBy",
            "lastUpdatedBy",
            "lastUpdatedAt",
            "status",
            "items",
            "tableId",
            "subtotal",
            "taxes",
            "areTipsIncluded",
            "total",
            "paymentDetails",
            "deliveryLocation",
            "client",
          ];

          const completedOrdersRef = doc(db, `organizations/${String(organizationId)}/completedOrders/${String(orderId)}`);

          const payload = {
            ...pick(order, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth?.user?.uid,
            lastUpdatedAt: Date.now(),
            completedBy: store.auth?.user?.uid,
            completedAt: Date.now(),
            status: OrderStatus.COMPLETED,
          };

          const response: any = await setDoc(completedOrdersRef, payload);
          const uncompletedOrdersRef = doc(db, `organizations/${String(organizationId)}/orders/${String(orderId)}`);
          await deleteDoc(uncompletedOrdersRef);

          return { data: response };
        } catch (error) {
          console.error(error);
          return { error };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Order"],
    }),
  }),
});

export const {
  useFetchOrdersQuery,
  useGetOrderByIdQuery,
  useUpdateOrderStatusMutation,
  useAddOrderMutation,
  useUpdateOrderMutation,
  useArchiveOrderMutation,
  useCompleteOrderMutation,
} = ordersApi;
